/**
 * SidebarNavEditor
 *
 * Property editor for sidebar-nav control type
 * Configures sidebar navigation appearance, behavior, and styling
 *
 * SRP: Responsible ONLY for configuring sidebar-nav properties
 * Control Type: 'sidebar-nav'
 *
 * Properties Configured (8):
 * 1. width - Sidebar width (px or %)
 * 2. backgroundColor - Sidebar background color
 * 3. textColor - Menu item text color
 * 4. activeItemColor - Active item highlight color
 * 5. collapsible - Allow collapse/expand
 * 6. position - Position (left/right)
 * 7. showIcons - Display menu icons
 * 8. animateOnHover - Hover effects
 */

import React, { useCallback, useMemo } from 'react';
import type { PropertyEditor, PropertyEditorProps } from '../../../registry/PropertyEditor.types';
import type { FormControl } from '@atlas-forms/types-js';
import { TextPropertyEditor } from '../BaseEditors/TextPropertyEditor';
import { ColorPropertyEditor } from '../BaseEditors/ColorPropertyEditor';
import { CheckboxPropertyEditor } from '../BaseEditors/CheckboxPropertyEditor';
import { SelectPropertyEditor } from '../BaseEditors/SelectPropertyEditor';

/**
 * Sidebar navigation configuration
 */
interface SidebarNavConfig {
  width?: string;
  backgroundColor?: string;
  textColor?: string;
  activeItemColor?: string;
  collapsible?: boolean;
  position?: 'left' | 'right';
  showIcons?: boolean;
  animateOnHover?: boolean;
}

/**
 * SidebarNavEditor Component
 * Renders property editors for all sidebar-nav configuration
 */
const SidebarNavEditorComponent: React.FC<PropertyEditorProps> = ({
  control,
  onConfigChange,
}) => {
  const config = useMemo(
    () => (control.config as SidebarNavConfig) || {},
    [control.config],
  );

  const handleWidthChange = useCallback(
    (value: string) => onConfigChange('width', value),
    [onConfigChange],
  );

  const handleBackgroundColorChange = useCallback(
    (value: string) => onConfigChange('backgroundColor', value),
    [onConfigChange],
  );

  const handleTextColorChange = useCallback(
    (value: string) => onConfigChange('textColor', value),
    [onConfigChange],
  );

  const handleActiveItemColorChange = useCallback(
    (value: string) => onConfigChange('activeItemColor', value),
    [onConfigChange],
  );

  const handleCollapsibleChange = useCallback(
    (value: boolean) => onConfigChange('collapsible', value),
    [onConfigChange],
  );

  const handlePositionChange = useCallback(
    (value: string) => onConfigChange('position', value as 'left' | 'right'),
    [onConfigChange],
  );

  const handleShowIconsChange = useCallback(
    (value: boolean) => onConfigChange('showIcons', value),
    [onConfigChange],
  );

  const handleAnimateOnHoverChange = useCallback(
    (value: boolean) => onConfigChange('animateOnHover', value),
    [onConfigChange],
  );

  const positionOptions = useMemo(
    () => [
      { label: 'Left', value: 'left' },
      { label: 'Right', value: 'right' },
    ],
    [],
  );

  return (
    <div className="af-property-editor__section sidebar-nav-editor">
      <h3 className="af-property-editor__section-title">Sidebar Navigation Properties</h3>

      <TextPropertyEditor
        label="Width"
        value={config.width || '250px'}
        onChange={handleWidthChange}
        placeholder="e.g., 250px or 20%"
        helpText="Sidebar width (px or %)"
      />

      <ColorPropertyEditor
        label="Background Color"
        value={config.backgroundColor || '#FFFFFF'}
        onChange={handleBackgroundColorChange}
        helpText="Sidebar background color"
      />

      <ColorPropertyEditor
        label="Text Color"
        value={config.textColor || '#000000'}
        onChange={handleTextColorChange}
        helpText="Menu item text color"
      />

      <ColorPropertyEditor
        label="Active Item Color"
        value={config.activeItemColor || '#309B48'}
        onChange={handleActiveItemColorChange}
        helpText="Highlight color for active menu item"
      />

      <CheckboxPropertyEditor
        label="Collapsible"
        value={config.collapsible ?? true}
        onChange={handleCollapsibleChange}
        helpText="Allow sidebar to collapse/expand"
      />

      <SelectPropertyEditor
        label="Position"
        value={config.position || 'left'}
        onChange={handlePositionChange}
        options={positionOptions}
        helpText="Sidebar placement"
      />

      <CheckboxPropertyEditor
        label="Show Icons"
        value={config.showIcons ?? true}
        onChange={handleShowIconsChange}
        helpText="Display icons next to menu items"
      />

      <CheckboxPropertyEditor
        label="Animate on Hover"
        value={config.animateOnHover || false}
        onChange={handleAnimateOnHoverChange}
        helpText="Enable hover effects and animations"
      />
    </div>
  );
};

SidebarNavEditorComponent.displayName = 'SidebarNavEditor';

/**
 * Sidebar Navigation Property Editor Registration
 */
export const SidebarNavEditor: PropertyEditor = {
  type: 'sidebar-nav',
  label: 'Sidebar Navigation',
  description: 'Configure sidebar navigation appearance, behavior, and styling',
  version: '1.0.0',
  render: (props) => <SidebarNavEditorComponent {...props} />,
};

export default SidebarNavEditor;
